import React, { useState, useEffect } from "react";
import { saveOrders, loadOrders, applyPartialReturn } from "../../data/orders";
import { adjustInventoryStock } from "../../data/inventory";
import { isEligibleForCoupon } from "../../data/coupons";
import CouponOfferModal from "./CouponOfferModal";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} €`;

const PaymentModal = ({ total, cart, clientName, clientPhone, orderMode, businessUnit = "alimentation", onComplete, onClose, onViewOrder }) => {
  const [payments, setPayments] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("ESPECES");
  const [step, setStep] = useState("PAYMENT");
  const [customerEmail, setCustomerEmail] = useState("");
  const [showCouponOffer, setShowCouponOffer] = useState(false);

  const orderId = React.useMemo(() => "CMD-" + Date.now().toString().slice(-6), []);
  const isRefund = total < 0;
  const absTotal = Math.abs(total);

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const cashPaid = payments.filter((p) => p.method === "ESPECES").reduce((sum, p) => sum + p.amount, 0);
  const remaining = isRefund ? Math.max(0, absTotal - totalPaid) : Math.max(0, total - totalPaid);
  const rawChange = isRefund ? Math.max(0, totalPaid - absTotal) : Math.max(0, totalPaid - total);
  const change = Math.min(rawChange, cashPaid); // Only give change up to the amount paid in cash
  const lostOverpayment = rawChange - change; // Amount overpaid via non-refundable methods

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (step !== "PAYMENT") return;
      if (e.key === "Escape") onClose();
      else if (e.key === "Enter") addPayment();
      else if (e.key === "," || e.key === ".") {
        e.preventDefault();
        handleInput(".");
      } else if (e.key === "Backspace") {
        handleBackspace();
      } else if (/^[0-9]$/.test(e.key)) {
        handleInput(e.key);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentInput, selectedMethod, step, remaining]);

  const handleInput = (char) => {
    if (char === "." && currentInput.includes(".")) return;
    setCurrentInput((prev) => prev + char);
  };

  const handleBackspace = () => setCurrentInput((prev) => prev.slice(0, -1));
  const handleClear = () => setCurrentInput("");

  const addPayment = () => {
    let amount = parseFloat(currentInput);
    if (isNaN(amount) || amount <= 0) {
      amount = remaining; // If empty, pay the exact remaining amount
    }
    if (amount <= 0) return;
    
    setPayments([...payments, { method: selectedMethod, amount, id: Date.now() }]);
    setCurrentInput("");
  };

  const removePayment = (id) => {
    setPayments(payments.filter((p) => p.id !== id));
  };

  const handleValidateSale = async () => {
    if (remaining > 0) return;
    const now = new Date();
const order = {
      id: orderId,
      orderNumber: orderId,
      businessUnit: businessUnit,
      date: now.toLocaleDateString("fr-FR"),
      time: now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
      timestamp: now.toISOString(),
      total: total,
      totalTVA: cart.items.reduce((sum, i) => sum + (i.tvaAmount || Math.abs(i.lineTotal) * 0.11), 0), 
      payments: payments.map(p => ({ method: p.method, amount: p.amount })),
items: cart.items.map(i => ({
        name: i.title,
        productId: i.product?.id || "",
        quantity: i.quantity,
        unitPrice: i.price,
        lineTotal: i.lineTotal,
        unitType: i.isWeighted ? "kg" : "piece",
        isReturn: i.isReturn || false,
        restockOnComplete: i.restockOnComplete || false,
        returnRef: i.returnRef || null,
        returnReason: i.returnReason || "",
        components: Array.isArray(i.product && i.product.components) ? i.product.components : [],
      })),
      client: clientName || null,
      clientPhone: clientPhone || null,
      isReturn: isRefund,
      refundedFrom: isRefund ? cart.items.find(i => i.isReturn)?.returnRef : null,
      orderMode: orderMode || 'magasin',
    };

    // Persist to localStorage using the app's orders module
    try {
      const existingOrders = await loadOrders();
      await saveOrders([order, ...existingOrders]);
    } catch (e) {
      // Fallback: save directly to correct storage key
      const allOrders = JSON.parse(localStorage.getItem("almoune_admin_orders") || "[]");
      allOrders.unshift(order);
      localStorage.setItem("almoune_admin_orders", JSON.stringify(allOrders));
    }

    // Adjust stock
    cart.items.forEach((i) => {
      if (i.isReturn) {
        if (i.restockOnComplete && i.product?.id) {
          adjustInventoryStock(i.product.id, Math.abs(i.quantity), "Retour client");
        }
      } else {
        if (i.product?.id) {
          adjustInventoryStock(i.product.id, -Math.abs(i.quantity), "Vente");
        }
      }
    });

    // Update original orders for any returned items that reference them
    const returnItemsWithRefs = cart.items.filter(i => i.isReturn && i.returnRef && i.returnRef !== "RETOUR_EXPRESS");
    if (returnItemsWithRefs.length > 0) {
      const groupedByRef = returnItemsWithRefs.reduce((acc, item) => {
        if (!acc[item.returnRef]) acc[item.returnRef] = [];
        acc[item.returnRef].push(item);
        return acc;
      }, {});
      
      for (const [ref, items] of Object.entries(groupedByRef)) {
        await applyPartialReturn(ref, items);
      }
    }

    setStep("SUCCESS");
  };

  const handlePrint = () => {
    window.print(); // Simple browser print, can be replaced with thermal printer logic
  };

  if (step === "SUCCESS") {
    return (
      <>
      <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1055 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
            <div className="modal-body p-5 text-center">
              <div className="mb-4 text-success">
                <i className="bi bi-check-circle-fill" style={{ fontSize: "4rem" }}></i>
              </div>
              <h3 className="fw-bold mb-1" style={{ color: "#1f2937" }}>{isRefund ? "Remboursement Validé !" : "Vente Validée !"}</h3>
              <p className="text-muted mb-4">Commande {orderId}</p>
              
              <div className="bg-light rounded p-3 mb-4 text-start">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">{isRefund ? "Total à rembourser :" : "Total à payer :"}</span>
                  <span className="fw-bold fs-5" style={{ color: "#1f2937" }}>{formatPrice(absTotal)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">{isRefund ? "Total remboursé :" : "Total Payé :"}</span>
                  <span className="fw-bold text-success">{formatPrice(totalPaid)}</span>
                </div>
                <div className="d-flex justify-content-between border-top pt-2 mt-2">
                  <span className="text-muted">{isRefund ? "Différence :" : "Rendu Monnaie :"}</span>
                  <span className="fw-bold text-danger">{formatPrice(change)}</span>
                </div>
                {!isRefund && lostOverpayment > 0 && (
                  <div className="d-flex justify-content-between pt-1">
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>Trop perçu (non rendu) :</span>
                    <span className="fw-bold text-warning" style={{ fontSize: "0.8rem" }}>{formatPrice(lostOverpayment)}</span>
                  </div>
                )}
              </div>

              <div className="d-flex flex-column gap-2 mb-4">
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-dark fw-bold flex-grow-1" onClick={() => onViewOrder && onViewOrder(orderId, total, payments)}>
                    <i className="bi bi-printer me-2"></i> TICKET
                  </button>
                  <button className="btn btn-outline-dark fw-bold flex-grow-1" onClick={() => onViewOrder && onViewOrder(orderId, total, payments)}>
                    <i className="bi bi-file-earmark-text me-2"></i> FACTURE
                  </button>
                </div>
                <div className="input-group mt-2">
                  <input type="email" className="form-control" placeholder="Email du client" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
                  <button className="btn btn-secondary fw-bold">FACTURE PAR EMAIL</button>
                </div>
              </div>

              {!isRefund && isEligibleForCoupon(total) && !showCouponOffer && (
                <button
                  className="btn w-100 py-2 fw-bold mb-2 d-flex align-items-center justify-content-center gap-2"
                  style={{ backgroundColor: "#fef3c7", color: "#92400e", borderRadius: "10px", border: "2px solid #fbbf24" }}
                  onClick={() => setShowCouponOffer(true)}
                >
                  <i className="bi bi-gift-fill"></i> Offrir un bon de réduction
                </button>
              )}

              <button className="btn w-100 py-3 fw-bold text-white fs-5" style={{ backgroundColor: "#22d3ee" }} onClick={onComplete}>
                {isRefund ? "NOUVELLE OPÉRATION" : "NOUVELLE VENTE"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <CouponOfferModal
        show={showCouponOffer}
        onClose={() => setShowCouponOffer(false)}
        orderId={orderId}
        total={total}
      />
      </>
    );
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1055 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
            <h4 className="modal-title fw-bold">{isRefund ? "REMBOURSEMENT" : "RÈGLEMENT"}</h4>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body p-4">
            <div className="row g-4">
              
              {/* Left Side: Summary & Applied Payments */}
              <div className="col-md-6 d-flex flex-column">
                <div className="bg-light p-3 rounded mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted fs-5">{isRefund ? "Total à rembourser" : "Total à payer"}</span>
                    <span className="fw-bold fs-4" style={{ color: "#1f2937" }}>{formatPrice(absTotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted fs-5">{isRefund ? "Reste à rembourser" : "Reste à payer"}</span>
                    <span className="fw-bold fs-3" style={{ color: "#dc3545" }}>{formatPrice(remaining)}</span>
                  </div>
                </div>

                <div className="flex-grow-1 overflow-auto border rounded p-2 bg-white" style={{ minHeight: "150px" }}>
                  {payments.length === 0 ? (
                    <div className="text-center text-muted opacity-50 mt-4">Aucun paiement ajouté</div>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {payments.map((p) => (
                        <li key={p.id} className="list-group-item d-flex justify-content-between align-items-center px-1">
                          <div>
                            <span className="badge bg-secondary me-2">{p.method}</span>
                            <span className="fw-bold">{formatPrice(p.amount)}</span>
                          </div>
                          <button className="btn btn-sm text-danger p-0" onClick={() => removePayment(p.id)}><i className="bi bi-x-circle-fill fs-5"></i></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {change > 0 && (
                  <div className="bg-success bg-opacity-25 text-dark p-3 rounded mt-3 d-flex justify-content-between align-items-center">
                    <span className="fw-bold">Rendu monnaie :</span>
                    <span className="fw-bold fs-4">{formatPrice(change)}</span>
                  </div>
                )}
                {lostOverpayment > 0 && (
                  <div className="bg-warning bg-opacity-25 text-dark p-2 rounded mt-2 d-flex justify-content-between align-items-center">
                    <span className="fw-bold" style={{ fontSize: "0.8rem" }}>Trop perçu (Avoir/TR) non rendu :</span>
                    <span className="fw-bold">{formatPrice(lostOverpayment)}</span>
                  </div>
                )}
              </div>

              {/* Right Side: Keypad & Methods */}
              <div className="col-md-6">
                <div className="row g-2 mb-3">
                  {["ESPECES", "CB", "CHEQUE", "VIREMENT", "TR", "AVOIR", "DIFFERE"].map(method => (
                    <div className="col-4" key={method}>
                      <button 
                        className={`btn w-100 py-2 fw-bold ${selectedMethod === method ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ fontSize: "0.8rem" }}
                        onClick={() => setSelectedMethod(method)}
                      >
                        {method}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="input-group mb-3">
                  <span className="input-group-text bg-white border-end-0 fs-4">€</span>
                  <input
                    type="text"
                    className="form-control form-control-lg border-start-0 fs-3 fw-bold text-end"
                    placeholder={remaining.toFixed(2)}
                    value={currentInput}
                    readOnly
                  />
                  <button className="btn btn-success fw-bold px-4" onClick={addPayment}>
                    AJOUTER
                  </button>
                </div>

                <div className="row g-2">
                  {[[7, 8, 9], [4, 5, 6], [1, 2, 3]].map((row, i) => (
                    <React.Fragment key={i}>
                      {row.map((num) => (
                        <div className="col-4" key={num}>
                          <button className="btn btn-light border w-100 py-3 fs-4 fw-bold" style={{ color: "#374151" }} onClick={() => handleInput(String(num))}>{num}</button>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                  <div className="col-4">
                    <button className="btn btn-light border w-100 py-3 fs-4 fw-bold" style={{ color: "#374151" }} onClick={() => handleInput("0")}>0</button>
                  </div>
                  <div className="col-4">
                    <button className="btn btn-light border w-100 py-3 fs-4 fw-bold" style={{ color: "#374151" }} onClick={() => handleInput("00")}>00</button>
                  </div>
                  <div className="col-4">
                    <button className="btn btn-light border w-100 py-3 fs-4 fw-bold" style={{ color: "#374151" }} onClick={() => handleInput(".")}>.</button>
                  </div>
                  <div className="col-6 mt-2">
                    <button className="btn btn-outline-danger w-100 py-3 fw-bold" onClick={handleBackspace}><i className="bi bi-backspace"></i> CORRIGER</button>
                  </div>
                  <div className="col-6 mt-2">
                    <button className="btn btn-outline-secondary w-100 py-3 fw-bold" onClick={handleClear}>EFFACER</button>
                  </div>
                </div>
              </div>

            </div>
          </div>
          
          <div className="modal-footer border-top-0 bg-light p-4 mt-2 d-flex justify-content-between">
            <button className="btn btn-secondary px-4 py-3 fw-bold" onClick={onClose}>RETOUR</button>
            <button 
              className={`btn px-5 py-3 fw-bold text-white fs-5 ${remaining <= 0 ? 'bg-success' : 'bg-secondary opacity-50'}`} 
              onClick={handleValidateSale}
              disabled={remaining > 0}
            >
              <i className="bi bi-check2-circle me-2"></i> {isRefund ? "VALIDER LE REMBOURSEMENT" : "VALIDER LA VENTE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;

